/**
 * Trusscore Estimator - Wall Calculations
 *
 * Orchestrates wall material calculations including:
 * - W&CB panels (horizontal or vertical orientation)
 * - SlatWall panels and trims
 * - Common wall trims (J-trim, base, corners)
 *
 * Supports both FullRoom mode (all walls same config) and
 * IndividualWalls mode (each wall configured separately).
 */

import {
  MaterialItem,
  ProductColor,
  WallConfig,
  CoreDimensions,
  RoomDimensions,
  FullRoomWallConfig,
  SurfaceBreakdown,
  SurfaceMaterials,
} from '../types';
import { calculateCoreDimensions, createFullRoomWallConfigs } from './dimensions';
import { calculateHorizontalPanels, calculateVerticalPanels, calculateVerticalStackingHTrims } from './panels';
import { calculateAllSlatWallMaterials, SlatWallResult, SlatWallOffcut } from './slatwall';
import { calculateCommonWallTrims, TrimResult } from './trims';

export interface WallResult {
  panels: MaterialItem[];
  slatWallPanels: MaterialItem[];
  hTrims: MaterialItem[];
  // Snap-In Trim J — produced by all current calc paths.
  jTrimsSnapIn: MaterialItem[];
  // Retained empty for back-compat with consumers that still read the legacy fields.
  jTrimsHalfInch: MaterialItem[];
  jTrimsThreeQuarter: MaterialItem[];
  baseTrims: MaterialItem[];
  insideCoveTrims: MaterialItem[];
  outsideCornerTrims: MaterialItem[];
  coreDimensions: CoreDimensions;
  totalPanelCount: number;
  totalSqft: number;
}

/**
 * Calculate all materials for a single wall.
 *
 * @param wallConfig - Wall configuration
 * @param commonRoomHeight - Room height for dimension calculations
 * @param includeCeiling - Whether ceiling is included (affects trims)
 * @returns Complete wall materials result
 */
export function calculateWallMaterials(
  wallConfig: WallConfig,
  commonRoomHeight: number,
  includeCeiling: boolean = false,
  slatWallOffcuts?: SlatWallOffcut[]
): WallResult {
  const emptyResult: WallResult = {
    panels: [],
    slatWallPanels: [],
    hTrims: [],
    jTrimsSnapIn: [],
    jTrimsHalfInch: [],
    jTrimsThreeQuarter: [],
    baseTrims: [],
    insideCoveTrims: [],
    outsideCornerTrims: [],
    coreDimensions: {
      wallNetLength: 0,
      wallheightBelowSW: 0,
      slatWallCalculatedHeight: 0,
      wallheightAboveSW: 0,
      heightAboveGarage: 0,
      commonRoomHeight,
    },
    totalPanelCount: 0,
    totalSqft: 0,
  };

  if (!wallConfig.enabled || wallConfig.length <= 0 || wallConfig.height <= 0) {
    return emptyResult;
  }

  // Calculate core dimensions
  const coreDims = calculateCoreDimensions(wallConfig, commonRoomHeight);

  // Initialize result
  const result: WallResult = {
    panels: [],
    slatWallPanels: [],
    hTrims: [],
    jTrimsSnapIn: [],
    jTrimsHalfInch: [],
    jTrimsThreeQuarter: [],
    baseTrims: [],
    insideCoveTrims: [],
    outsideCornerTrims: [],
    coreDimensions: coreDims,
    totalPanelCount: 0,
    totalSqft: 0,
  };

  // Calculate W&CB panels based on orientation
  if (wallConfig.orientation === 'Horizontal') {
    // For horizontal orientation with garage doors:
    // Calculate wall sections using netLength (wall length minus garage door width)
    // Then add header section above garage door (full wall length × heightAboveGarage)
    //
    // For walls without garage: Standard 3-section breakdown (below SW, SW, above SW)

    // Calculate W&CB sections using netLength (same pattern for garage and non-garage walls)
    // Section 1: W&CB below SlatWall (or "beside door" for garage walls without SlatWall)
    if (coreDims.wallheightBelowSW > 0 && coreDims.wallNetLength > 0) {
      // For walls with garage doors, the "beside door" section only extends up to the garage door height
      // (the area above the door is covered by Section 3 at full wall width)
      // For walls without garage: use full wallheightBelowSW
      let effectiveBelowSWHeight = coreDims.wallheightBelowSW;

      if (wallConfig.garage.enabled && wallConfig.garage.height > 0 && !wallConfig.slatWall.enabled) {
        // No SlatWall: "beside door" section only goes up to garage door height
        effectiveBelowSWHeight = Math.min(coreDims.wallheightBelowSW, wallConfig.garage.height);
      } else if (wallConfig.garage.enabled && wallConfig.garage.height > 0 && wallConfig.slatWall.enabled) {
        // With SlatWall: "below SW beside door" section goes from floor to min(SlatWall start, garage height)
        effectiveBelowSWHeight = Math.min(coreDims.wallheightBelowSW, wallConfig.garage.height);
      }
      // For walls without garage: effectiveBelowSWHeight remains wallheightBelowSW (unchanged)

      const belowSWPanels = calculateHorizontalPanels(
        coreDims.wallNetLength,
        effectiveBelowSWHeight,
        wallConfig.color,
        null,
        `${wallConfig.name}_BelowSW`
      );
      result.panels.push(...belowSWPanels.panels);
      result.hTrims.push(...belowSWPanels.hTrims);
      result.totalPanelCount += belowSWPanels.totalPanelCount;
    }

    // Section 2: W&CB above SlatWall
    // For garage walls with SlatWall, the "above SW" section beside the garage only extends
    // from SlatWall end to the garage door top, NOT the full wallheightAboveSW.
    // The area from garage door top to ceiling is covered by the "above garage" section (full width).
    if (coreDims.wallheightAboveSW > 0 && coreDims.wallNetLength > 0) {
      let effectiveAboveSWHeight = coreDims.wallheightAboveSW;

      // If garage exists and SlatWall is enabled, calculate the correct height for "beside garage" section
      if (wallConfig.garage.enabled && wallConfig.garage.height > 0 && wallConfig.slatWall.enabled) {
        // SlatWall ends at: startHeight + numRows * 0.5 (6" per row)
        const slatWallEndHeight = wallConfig.slatWall.startHeight + coreDims.slatWallCalculatedHeight;
        // The "above SW beside garage" section only goes up to the garage door top
        // If garage door top (height) > slatWall end, this section = garageDoorHeight - slatWallEndHeight
        // If garage door top < slatWall end, this section = 0 (covered by SlatWall)
        if (wallConfig.garage.height > slatWallEndHeight) {
          effectiveAboveSWHeight = wallConfig.garage.height - slatWallEndHeight;
        } else {
          // Garage door doesn't extend above SlatWall, so no "above SW beside garage" section
          effectiveAboveSWHeight = 0;
        }
      }

      if (effectiveAboveSWHeight > 0) {
        const aboveSWPanels = calculateHorizontalPanels(
          coreDims.wallNetLength,
          effectiveAboveSWHeight,
          wallConfig.color,
          null,
          `${wallConfig.name}_AboveSW`
        );
        result.panels.push(...aboveSWPanels.panels);
        result.hTrims.push(...aboveSWPanels.hTrims);
        result.totalPanelCount += aboveSWPanels.totalPanelCount;
      }
    }

    // Section 3: Header above garage door (full wall length, if garage exists)
    if (wallConfig.garage.enabled && coreDims.heightAboveGarage > 0) {
      const aboveGaragePanels = calculateHorizontalPanels(
        wallConfig.length,
        coreDims.heightAboveGarage,
        wallConfig.color,
        null,
        `${wallConfig.name}_AboveGarage`
      );
      result.panels.push(...aboveGaragePanels.panels);
      result.hTrims.push(...aboveGaragePanels.hTrims);
      result.totalPanelCount += aboveGaragePanels.totalPanelCount;
    }

  } else {
    // Vertical orientation
    // Calculate vertical panels for each W&CB section

    // Section below SlatWall
    if (coreDims.wallheightBelowSW > 0) {
      const belowSWPanels = calculateVerticalPanels(
        coreDims.wallNetLength,
        coreDims.wallheightBelowSW,
        wallConfig.color,
        'WCB_Below_SW'
      );
      result.panels.push(...belowSWPanels);

      // Check for vertical stacking H-trim (height > 20')
      const stackingHTrims = calculateVerticalStackingHTrims(
        coreDims.wallheightBelowSW,
        coreDims.wallNetLength,
        wallConfig.color
      );
      result.hTrims.push(...stackingHTrims);
    }

    // Section above SlatWall
    if (coreDims.wallheightAboveSW > 0) {
      const aboveSWPanels = calculateVerticalPanels(
        coreDims.wallNetLength,
        coreDims.wallheightAboveSW,
        wallConfig.color,
        'WCB_Above_SW'
      );
      result.panels.push(...aboveSWPanels);

      // Check for vertical stacking H-trim (height > 20')
      const stackingHTrims = calculateVerticalStackingHTrims(
        coreDims.wallheightAboveSW,
        coreDims.wallNetLength,
        wallConfig.color
      );
      result.hTrims.push(...stackingHTrims);
    }

    // Section above garage (if applicable)
    if (coreDims.heightAboveGarage > 0 && wallConfig.garage.enabled) {
      const garageWidth = wallConfig.garage.width * wallConfig.garage.numDoors;
      const aboveGaragePanels = calculateVerticalPanels(
        garageWidth,
        coreDims.heightAboveGarage,
        wallConfig.color,
        'WCB_Above_Garage'
      );
      result.panels.push(...aboveGaragePanels);

      // Check for vertical stacking H-trim (height > 20')
      // Note: Above garage section unlikely to exceed 20', but check anyway
      const stackingHTrims = calculateVerticalStackingHTrims(
        coreDims.heightAboveGarage,
        garageWidth,
        wallConfig.color
      );
      result.hTrims.push(...stackingHTrims);
    }

    result.totalPanelCount = result.panels.reduce((sum, p) => sum + p.quantity, 0);
  }

  // Calculate SlatWall materials
  if (wallConfig.slatWall.enabled) {
    const slatWallResult = calculateAllSlatWallMaterials(coreDims, wallConfig.slatWall, slatWallOffcuts);
    result.slatWallPanels.push(...slatWallResult.panels);
    result.hTrims.push(...slatWallResult.hTrims);
    result.jTrimsThreeQuarter.push(...slatWallResult.jTrims);
  }

  // Calculate common wall trims
  const trimResult = calculateCommonWallTrims(coreDims, wallConfig, includeCeiling);
  result.jTrimsSnapIn.push(...trimResult.jTrimsSnapIn);
  result.jTrimsHalfInch.push(...trimResult.jTrimsHalfInch);
  result.jTrimsThreeQuarter.push(...trimResult.jTrimsThreeQuarter);
  result.baseTrims.push(...trimResult.baseTrims);
  result.insideCoveTrims.push(...trimResult.insideCoveTrims);
  result.outsideCornerTrims.push(...trimResult.outsideCornerTrims);
  result.hTrims.push(...trimResult.hTrims);

  // Calculate total sqft
  result.totalSqft = result.panels.reduce((sum, p) => sum + p.quantity * p.sqft, 0);

  return result;
}

/**
 * Calculate materials for multiple walls.
 *
 * @param wallConfigs - Array of wall configurations
 * @param commonRoomHeight - Room height
 * @param includeCeiling - Whether ceiling is included
 * @returns Array of wall results
 */
export function calculateMultipleWalls(
  wallConfigs: WallConfig[],
  commonRoomHeight: number,
  includeCeiling: boolean = false
): WallResult[] {
  const offcuts: SlatWallOffcut[] = [];
  const results: WallResult[] = [];
  for (const config of wallConfigs) {
    // Use each wall's own height if available, fall back to commonRoomHeight
    const wallHeight = config.height > 0 ? config.height : commonRoomHeight;
    results.push(calculateWallMaterials(config, wallHeight, includeCeiling, offcuts));
  }
  return results;
}

/**
 * Calculate all walls for FullRoom mode.
 *
 * Creates 4 walls based on room dimensions and applies
 * the same configuration to all walls.
 *
 * @param roomDimensions - Room dimensions
 * @param fullRoomConfig - Full room wall configuration
 * @param includeCeiling - Whether ceiling is included
 * @returns Array of wall results for all 4 walls
 */
export function calculateFullRoomWalls(
  roomDimensions: RoomDimensions,
  fullRoomConfig: FullRoomWallConfig,
  includeCeiling: boolean = false
): WallResult[] {
  // Create wall configs for all 4 walls
  const wallConfigs = createFullRoomWallConfigs(roomDimensions, fullRoomConfig);

  return calculateMultipleWalls(wallConfigs, roomDimensions.height, includeCeiling);
}

/**
 * Convert WallResult to SurfaceBreakdown format.
 *
 * @param wallResult - Wall calculation result
 * @param wallConfig - Original wall configuration
 * @returns Surface breakdown for results display
 */
export function wallResultToSurfaceBreakdown(
  wallResult: WallResult,
  wallConfig: WallConfig
): SurfaceBreakdown {
  // Combine all panels
  const allPanels: MaterialItem[] = [
    ...wallResult.panels,
    ...wallResult.slatWallPanels,
  ];

  // Combine all trims (excluding Inside Cove Trim which is not in estimator scope)
  const allTrims: MaterialItem[] = [
    ...wallResult.hTrims,
    ...wallResult.jTrimsSnapIn,
    ...wallResult.jTrimsHalfInch,
    ...wallResult.jTrimsThreeQuarter,
    ...wallResult.baseTrims,
    ...wallResult.outsideCornerTrims,
  ];

  const materials: SurfaceMaterials = {
    panels: allPanels,
    trims: allTrims,
  };

  return {
    surfaceId: wallConfig.wallId,
    surfaceName: wallConfig.name,
    enabled: wallConfig.enabled,
    config: wallConfig,
    coreDimensions: wallResult.coreDimensions,
    materials,
  };
}

/**
 * Aggregate wall results for consolidation.
 *
 * @param wallResults - Array of individual wall results
 * @returns Aggregated materials from all walls
 */
export function aggregateWallResults(wallResults: WallResult[]): {
  panels: MaterialItem[];
  slatWallPanels: MaterialItem[];
  hTrims: MaterialItem[];
  jTrimsSnapIn: MaterialItem[];
  jTrimsHalfInch: MaterialItem[];
  jTrimsThreeQuarter: MaterialItem[];
  baseTrims: MaterialItem[];
  insideCoveTrims: MaterialItem[];
  outsideCornerTrims: MaterialItem[];
  totalPanelCount: number;
  totalSqft: number;
} {
  const result = {
    panels: [] as MaterialItem[],
    slatWallPanels: [] as MaterialItem[],
    hTrims: [] as MaterialItem[],
    jTrimsSnapIn: [] as MaterialItem[],
    jTrimsHalfInch: [] as MaterialItem[],
    jTrimsThreeQuarter: [] as MaterialItem[],
    baseTrims: [] as MaterialItem[],
    insideCoveTrims: [] as MaterialItem[],
    outsideCornerTrims: [] as MaterialItem[],
    totalPanelCount: 0,
    totalSqft: 0,
  };

  for (const wallResult of wallResults) {
    result.panels.push(...wallResult.panels);
    result.slatWallPanels.push(...wallResult.slatWallPanels);
    result.hTrims.push(...wallResult.hTrims);
    result.jTrimsSnapIn.push(...wallResult.jTrimsSnapIn);
    result.jTrimsHalfInch.push(...wallResult.jTrimsHalfInch);
    result.jTrimsThreeQuarter.push(...wallResult.jTrimsThreeQuarter);
    result.baseTrims.push(...wallResult.baseTrims);
    result.insideCoveTrims.push(...wallResult.insideCoveTrims);
    result.outsideCornerTrims.push(...wallResult.outsideCornerTrims);
    result.totalPanelCount += wallResult.totalPanelCount;
    result.totalSqft += wallResult.totalSqft;
  }

  return result;
}

export default {
  calculateWallMaterials,
  calculateMultipleWalls,
  calculateFullRoomWalls,
  wallResultToSurfaceBreakdown,
  aggregateWallResults,
};
